import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Backdrop,
  Fade,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { DOWNLOAD_LICENSE_ENDPOINT } from "../config";

interface DownloadFileProps {
  open: boolean;
  onClose: () => void;
}

function DownloadFile({ open, onClose }: DownloadFileProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [requestId, setRequestId] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleClose = () => {
    setRequestId("");
    setError(null);
    setSuccess(null);
    setIsDownloading(false);
    onClose();
  };

  const handleDownloadFile = async () => {
    if (!requestId.trim()) {
      setError("Please enter a valid Request ID");
      return;
    }

    setIsDownloading(true);
    setError(null);
    setSuccess(null);

    try {
      // Since backend sets proper headers, we can simply open the URL
      const downloadUrl = `${DOWNLOAD_LICENSE_ENDPOINT}/${requestId.trim()}`;

      // Check if the file exists first with a HEAD request
      const response = await fetch(downloadUrl, {
        method: "HEAD",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("File not found. Please check your Request ID.");
        } else {
          throw new Error(`Download failed: ${response.statusText}`);
        }
      }

      // Open download URL - browser will handle the download automatically
      // due to backend Content-Disposition: attachment header
      // window.open(downloadUrl, "_blank");

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", ""); // Let browser use filename from headers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess("Download started successfully!");
      console.log("Download initiated for Request ID:", requestId.trim());

      // Auto close after successful download initiation
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(error instanceof Error ? error.message : "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRequestIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRequestId(event.target.value);
    // Clear error when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isSmallScreen ? "85%" : isMediumScreen ? "60%" : "40%",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: isSmallScreen ? 2 : 3,
            outline: "none",
            maxHeight: isSmallScreen ? "80vh" : "70vh",
            overflow: "auto",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: isSmallScreen ? 2 : 3,
            }}
          >
            <Typography
              variant={isSmallScreen ? "h6" : "h5"}
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              Download License File
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Request ID Input */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Request ID"
              placeholder="Enter your Request ID (e.g., CBBT755E)"
              value={requestId}
              onChange={handleRequestIdChange}
              disabled={isDownloading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#2BB132",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2BB132",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#2BB132",
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Enter the Request ID you received after uploading your file
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Modal Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isDownloading}
              sx={{
                borderColor: "#ccc",
                color: "#666",
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDownloadFile}
              disabled={!requestId.trim() || isDownloading}
              startIcon={
                isDownloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileDownloadIcon fontSize="small" />
                )
              }
              sx={{
                backgroundColor: "#2196F3",
                "&:hover": {
                  backgroundColor: "#1976D2",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                },
              }}
            >
              {isDownloading ? "Downloading..." : "Download File"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

export default DownloadFile;
